import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/hooks/use-auth';
import { MainWrap } from '@/styles/common';
import { useQuery } from '@apollo/client';
import { ROLE, TenantType } from '@trace-8/trace8-schema';
import axios from 'axios';
import fileDownload from 'js-file-download';
import { useEffect, useState } from 'react';
import Button from '../basicComponents/button';
import Header from '../header';
import { LoaderOnWholeScreen } from '../loaders/loader-on-whole-screen';
import { SettingsContainer, SettingsItem, SettingsItemButtonWrapper, SettingsItemTitle, SettingsWrapper } from '../settings-page';
import GenerateReportFailure from '../settings-page/superAdminSettings/modals/generate-report-failure';
import GeneratingReport from '../settings-page/superAdminSettings/modals/generating-report';
import {
  COMPLIANCE_PAGE_DEAL_DOCUMENTS_EMAIL_TRACKING_LIST_TOTAL_QUERY,
  GET_ALLOCATION_COMPLIANCE_TOTAL_QUERY,
  GET_DUE_DILIGENCE_COMPLIANCE_RECORDS_TOTAL_QUERY
} from './queries';

const allComplianceItems = {
  allocationHistory: {
    title: 'Allocation History',
    path: 'allocationHistory'
  },
  dueDiligenceHistory: {
    title: 'Due Diligence History',
    path: 'dueDiligenceHistory'
  },
  sendDealDocumentsTracking: {
    title: 'Send Deal Documents Tracking',
    path: 'sendDealDocumentsTracking'
  }
};

interface IComplianceItem {
  title: string;
  path: string;
}

interface IComplianceExportItem {
  title: string;
  onClick: () => void;
}

export const Compliance = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [complianceItems, setComplianceItems] = useState<IComplianceItem[]>([]);
  const [complianceExportItems, setComplianceExportItems] = useState<IComplianceExportItem[]>([]);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'failure' });

  const { data: listAllocationComplianceTotalData, loading: listAllocationComplianceTotalLoading } = useQuery<{
    ListAllocationCompliance: { total: number };
  }>(GET_ALLOCATION_COMPLIANCE_TOTAL_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      rowsPerPage: 10,
      page: 1
    }
  });

  const { data: listDueDiligenceComplianceRecordsTotalData, loading: listDueDiligenceComplianceRecordsTotalLoading } = useQuery<{
    listDueDiligenceComplianceRecords: { total: number };
  }>(GET_DUE_DILIGENCE_COMPLIANCE_RECORDS_TOTAL_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: user?.tenant?.type === TenantType.ADVISORY_FIRM,
    variables: {
      data: {
        statusFilter: ['Approved', 'Rejected']
      }
    }
  });

  const { data: listDealDocumentsEmailTrackingTotalData, loading: listDealDocumentsEmailTrackingTotalLoading } = useQuery<{
    dealDocumentsEmailTrackingList: { total: number };
  }>(COMPLIANCE_PAGE_DEAL_DOCUMENTS_EMAIL_TRACKING_LIST_TOTAL_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      data: {
        perPage: 10,
        page: 1
      }
    }
  });

  const handleDownloadTransactionsInvestorReport = async () => {
    setModalWindow({ isOpen: true, type: 'generating-report' });
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/allocation/investor-transactions-report`, {
        headers: { authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      fileDownload(response.data, 'investor-transactions-report.csv');
      setModalWindow({ isOpen: false, type: 'generating-report' });
    } catch (error) {
      setModalWindow({ isOpen: false, type: 'generating-report' });
      setModalWindow({ isOpen: true, type: 'generate-report-failure' });
    }
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
  };

  useEffect(() => {
    const isLoading =
      listAllocationComplianceTotalLoading || listDueDiligenceComplianceRecordsTotalLoading || listDealDocumentsEmailTrackingTotalLoading;
    if (isLoading) return;

    const hasAllocationHistory = listAllocationComplianceTotalData?.ListAllocationCompliance.total > 0;
    const hasDueDiligenceHistory = listDueDiligenceComplianceRecordsTotalData?.listDueDiligenceComplianceRecords.total > 0;
    const hasSendDealDocumentsTracking = listDealDocumentsEmailTrackingTotalData?.dealDocumentsEmailTrackingList.total > 0;

    const updatedComplianceItems: IComplianceItem[] = [];

    if (hasAllocationHistory) {
      updatedComplianceItems.push(allComplianceItems.allocationHistory);
    }
    if (hasDueDiligenceHistory) {
      updatedComplianceItems.push(allComplianceItems.dueDiligenceHistory);
    }
    if (hasSendDealDocumentsTracking) {
      updatedComplianceItems.push(allComplianceItems.sendDealDocumentsTracking);
    }

    setComplianceItems(updatedComplianceItems);
  }, [
    listAllocationComplianceTotalLoading,
    listAllocationComplianceTotalData,
    listDueDiligenceComplianceRecordsTotalLoading,
    listDueDiligenceComplianceRecordsTotalData,
    listDealDocumentsEmailTrackingTotalLoading,
    listDealDocumentsEmailTrackingTotalData
  ]);

  useEffect(() => {
    if (user?.tenant?.type === TenantType.PLATFORM_MANAGER && user?.roles?.some((role) => role === ROLE.SUPER_ADMIN || role === ROLE.ADMIN)) {
      setComplianceExportItems([{ title: 'Export Transactions Investor Report', onClick: handleDownloadTransactionsInvestorReport }]);
    }
  }, [user]);

  const openPage = (path: string) => {
    navigate(`/compliance/${path}?backToTitle=Compliance`);
  };

  if (listAllocationComplianceTotalLoading || listDueDiligenceComplianceRecordsTotalLoading || listDealDocumentsEmailTrackingTotalLoading) {
    return <LoaderOnWholeScreen />;
  }

  return (
    <>
      {modalWindow.type === 'generating-report' && <GeneratingReport isOpen={modalWindow.isOpen} />}
      {modalWindow.type === 'generate-report-failure' && <GenerateReportFailure isOpen={modalWindow.isOpen} onClose={closeModalWindow} />}
      <MainWrap>
        <Header />
        <SettingsContainer>
          <SettingsWrapper>
            {complianceItems.map((item) => (
              <SettingsItem key={item.path}>
                <SettingsItemTitle id="itemTitle" onClick={() => openPage(item.path)}>
                  {item.title}
                </SettingsItemTitle>
                <SettingsItemButtonWrapper id="openButton">
                  <Button onClick={() => openPage(item.path)}>Open</Button>
                </SettingsItemButtonWrapper>
              </SettingsItem>
            ))}
            {complianceExportItems.map((item) => (
              <SettingsItem key={item.title}>
                <SettingsItemTitle id="itemTitle" onClick={() => item.onClick()}>
                  {item.title}
                </SettingsItemTitle>
                <SettingsItemButtonWrapper id="openButton">
                  <Button onClick={() => item.onClick()}>Download</Button>
                </SettingsItemButtonWrapper>
              </SettingsItem>
            ))}
          </SettingsWrapper>
        </SettingsContainer>
      </MainWrap>
    </>
  );
};
